/**
 * Valores de parcela personalizados (empréstimos de Valor Fixo).
 *
 * Retrocompatível: quando `installment_values` é nulo/inválido, o cálculo
 * volta a ser exatamente o atual — valor total ÷ quantidade de parcelas.
 */

export const round2 = (v: number): number => Math.round((Number(v) || 0) * 100) / 100;

export interface InstallmentLoanLike {
  amount: number;
  installments: number;
  installmentValues?: unknown;
}

/**
 * Valida a lista de valores personalizados.
 * Precisa ser um array de números finitos > 0 com tamanho igual a `installments`.
 * Qualquer coisa fora disso retorna `null` (fallback total ao comportamento atual).
 */
export function parseInstallmentValues(raw: unknown, installments?: number): number[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const values: number[] = [];
  for (const item of raw) {
    const n = typeof item === "string" ? parseFloat(item) : Number(item);
    if (!Number.isFinite(n) || n <= 0) return null;
    values.push(round2(n));
  }
  if (typeof installments === "number" && installments > 0 && values.length !== installments) {
    return null;
  }
  return values;
}

/** Valor da parcela na posição `index` (0-based). */
export function getInstallmentAmount(loan: InstallmentLoanLike, index: number): number {
  const total = Number(loan.amount) || 0;
  const count = Number(loan.installments) || 0;
  const values = parseInstallmentValues(loan.installmentValues, count);
  if (values && index >= 0 && index < values.length) return round2(values[index]);
  if (count > 0) return round2(total / count);
  return round2(total);
}

/** Soma de uma lista de valores de parcela. */
export function sumInstallments(values: Array<number | string>): number {
  return round2(
    values.reduce<number>((sum, v) => {
      const n = typeof v === "string" ? parseFloat(v) : Number(v);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0),
  );
}

/** Saldo restante: soma apenas das parcelas ainda não pagas. */
export function remainingBalance(loan: InstallmentLoanLike, paidInstallments: number): number {
  const count = Number(loan.installments) || 0;
  const paid = Math.max(0, Math.min(Number(paidInstallments) || 0, count));
  let total = 0;
  for (let i = paid; i < count; i++) total += getInstallmentAmount(loan, i);
  return round2(total);
}

/** Lista completa de valores (personalizados ou divididos igualmente). */
export function installmentList(loan: InstallmentLoanLike): number[] {
  const count = Number(loan.installments) || 0;
  return Array.from({ length: count }, (_, i) => getInstallmentAmount(loan, i));
}

/** Verdadeiro quando o empréstimo possui valores personalizados válidos. */
export function hasCustomInstallments(loan: InstallmentLoanLike): boolean {
  return parseInstallmentValues(loan.installmentValues, Number(loan.installments) || 0) !== null;
}
